import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import MedicalInfoDrawer from '../drawers/MedicalInfoDrawer';
import MedicalReasonDrawer from '../drawers/MedicalReasonDrawer';
import PatientVerificationDrawer from '../drawers/PatientVerificationDrawer';
import { resendProfessionalEmail } from '~/services/campaign';

interface Props {
    patient?: any;
    campaign?: any;
    campaignRequest?: any;
    metadata?: any;
    application?: any;
    onDataUpdate?: () => void;
    campaignDispatch?: any;
}

const MedicalInfoTab: React.FC<Props> = ({
    metadata,
    patient,
    campaign,
    campaignRequest,
    application,
    onDataUpdate,
    campaignDispatch,
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isMedicalReasonDrawerOpen, setIsMedicalReasonDrawerOpen] = useState(false);
    const [isVerifyDrawerOpen, setIsVerifyDrawerOpen] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [emailSentDate, setEmailSentDate] = useState<string | null>(null);

    const hasMedProEmail = !!patient?.medical_professional?.person?.email;

    // Once the Medical Verification is complete there's nothing left to verify,
    // so the Resend Email button should fall off (HHLTB-2383). A completion date
    // is only set when the MV is done.
    const isMvComplete = !!patient?.verification_completion_date;

    const displayEmailSentDate =
        emailSentDate ?? patient?.email_sent_to_medical_professional_date;

    const handleResendEmail = async () => {
        const campaignId = campaign?.campaign_id || campaign?.id;
        if (!campaignId) return;

        setIsResending(true);
        try {
            const result = await resendProfessionalEmail(campaignId);
            if (result.email_sent_to_medical_professional_date) {
                setEmailSentDate(result.email_sent_to_medical_professional_date);
            }
            toast.success('Verification email resent successfully');
            onDataUpdate?.();
        } catch (err: any) {
            toast.error(err.detail || err.message || 'Failed to resend email');
        } finally {
            setIsResending(false);
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return null;
        try {
            return format(parseISO(dateStr), 'MMMM d, yyyy');
        } catch {
            return dateStr;
        }
    };

    // Derive date label from the primary medical reason code
    const medicalReasonCode = patient?.primary_medical_issue?.medical_reason?.code;
    const dateLabel = medicalReasonCode === 'transplant'
        ? 'Transplant Date'
        : medicalReasonCode === 'cat_injury'
            ? 'Accident Date'
            : medicalReasonCode === 'cat_illness'
                ? 'Illness Date'
                : 'Medical Issue Date';

    return (
        <>
            <div className='p-[12px] pt-0'>
                <div className='flex items-center justify-between mb-4'>
                    <h2 className='text-[22px]/[28px] font-bold'>
                        Medical Info
                    </h2>
                    <button
                        onClick={() => setIsMedicalReasonDrawerOpen(true)}
                        className='rounded-full border-[2px] border-black px-[20px] py-[4px] text-[12px] font-bold uppercase hover:bg-black hover:text-white transition-colors'
                    >
                        Edit
                    </button>
                </div>

                <h3 className='text-[16px] font-bold uppercase mb-4'>
                    Primary Medical Reason
                </h3>
                <div className='flex justify-between'>
                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Medical Reason
                            </label>
                            <div>{patient?.primary_medical_issue?.medical_reason?.name}</div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Injury Type
                            </label>
                            <div>{patient?.primary_medical_issue?.name}</div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Diagnosis
                            </label>
                            <div>{patient?.transplant_diagnosis?.name}</div>
                        </div>
                        {medicalReasonCode === 'transplant' && (
                            <div className='mb-[16px]'>
                                <label className='text-[12px]/[16px] text-[#949599]'>
                                    Living Donor
                                </label>
                                <div>{patient?.living_donor ? 'Yes' : 'No'}</div>
                            </div>
                        )}
                    </div>
                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Timeframe
                            </label>
                            <div>{patient?.injury_time_frame?.name}</div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                {dateLabel}
                            </label>
                            <div>{formatDate(patient?.medical_issue_date)}</div>
                        </div>
                        {medicalReasonCode === 'transplant' && (
                            <>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Candidacy Status
                                    </label>
                                    <div>{patient?.candidacy_status?.name}</div>
                                </div>
                                {patient?.candidacy_status?.code === 'listed' && (
                                    <div className='mb-[16px]'>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Listing Date
                                        </label>
                                        <div>{formatDate(patient?.listing_date)}</div>
                                    </div>
                                )}
                            </>
                        )}
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Insurance
                            </label>
                            <div>{campaignRequest?.insurance_type?.name}</div>
                        </div>
                    </div>
                </div>

                {patient?.notes && (
                    <div className='mb-[16px] mt-4'>
                        <label className='text-[12px]/[16px] text-[#949599]'>
                            Notes
                        </label>
                        <div className='whitespace-pre-wrap'>{patient.notes}</div>
                    </div>
                )}

                {patient?.secondary_medical_issue && (
                    <>
                        <h3 className='text-[16px] font-bold uppercase mb-4 mt-4'>
                            Secondary Medical Reason
                        </h3>
                        <div className='flex justify-between'>
                            <div className='grow'>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Medical Reason
                                    </label>
                                    <div>{patient?.secondary_medical_issue?.medical_reason?.name}</div>
                                </div>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Injury Type
                                    </label>
                                    <div>{patient?.secondary_medical_issue?.name}</div>
                                </div>
                            </div>
                            <div className='grow'>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Timeframe
                                    </label>
                                    <div>{patient?.injury_time_frame?.name}</div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <hr className='my-6' />
                <div className='mb-[16px]'>
                    <label className='text-[12px]/[16px] text-[#949599]'>
                        Medical Professional
                    </label>
                    <div className='flex items-center gap-2'>
                        <div>
                            {patient?.medical_professional?.person
                                ?.email ? (
                                <a
                                    className='text-primary hover:bg-opacity-90'
                                    href={`mailto:${patient?.medical_professional?.person?.email}`}
                                >
                                    {
                                        patient?.medical_professional
                                            ?.person?.first_name
                                    }{' '}
                                    {
                                        patient?.medical_professional
                                            ?.person?.last_name
                                    }{' '}
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='18'
                                        height='18'
                                        viewBox='0 0 18 18'
                                        fill='none'
                                    >
                                        <path
                                            d='M4.05 15L3 13.95L11.7 5.25H6.75V3.75H14.25V11.25H12.75V6.3L4.05 15Z'
                                            fill='#00AEB3'
                                        />
                                    </svg>
                                </a>
                            ) : (
                                <>
                                    {
                                        patient?.medical_professional
                                            ?.person?.first_name
                                    }{' '}
                                    {
                                        patient?.medical_professional
                                            ?.person?.last_name
                                    }
                                </>
                            )}
                        </div>
                        <button
                            onClick={() => setIsDrawerOpen(true)}
                            className='text-gray-500 hover:text-gray-700'
                            title='Edit Medical Professional'
                        >
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='16'
                                height='16'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            >
                                <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
                                <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
                            </svg>
                        </button>
                    </div>
                    <div>
                        {
                            patient?.medical_professional
                                ?.medical_center?.name
                        }
                    </div>
                    {patient?.medical_professional?.person?.email && (
                        <div>
                            <a
                                className='text-primary hover:bg-opacity-90'
                                href={`mailto:${patient?.medical_professional?.person?.email}`}
                            >
                                {
                                    patient?.medical_professional
                                        ?.person?.email
                                }
                            </a>
                        </div>
                    )}
                </div>
                <div className='flex justify-between'>
                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Medical Verification Status
                            </label>
                            <div className='mt-2'>
                                {patient?.report_verification_status_code && (
                                    <div className='inline-flex rounded-full bg-primary px-2 py-1 text-sm'>
                                        {
                                            patient?.report_verification_status_code
                                        }
                                    </div>
                                )}
                            </div>
                            {!isMvComplete && patient?.id && (
                                <div className='mt-2'>
                                    <button
                                        onClick={() =>
                                            setIsVerifyDrawerOpen(true)
                                        }
                                        title='Manually verify this patient'
                                        className='inline-flex items-center rounded-full bg-warning px-4 py-1 text-sm font-bold uppercase text-black hover:opacity-90'
                                    >
                                        Verify Now
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Medical Verification Date
                            </label>
                            <div>
                                {formatDate(patient?.verification_completion_date)}
                            </div>
                        </div>
                    </div>
                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Email Sent
                            </label>
                            <div>
                                {displayEmailSentDate
                                    ? format(
                                          parseISO(displayEmailSentDate),
                                          'PPP'
                                      )
                                    : 'Not yet sent'}
                            </div>
                            {!isMvComplete && (
                            <div className='mt-2'>
                                <button
                                    onClick={handleResendEmail}
                                    disabled={!hasMedProEmail || isResending}
                                    title={
                                        !hasMedProEmail
                                            ? 'Medical professional has no email address'
                                            : 'Resend verification email'
                                    }
                                    className={`inline-flex items-center gap-2 rounded-full px-4 py-1 text-sm font-medium ${
                                        hasMedProEmail && !isResending
                                            ? 'bg-[#c8d96f] text-black hover:bg-[#b8c95f] cursor-pointer'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    {isResending && (
                                        <svg
                                            className='h-4 w-4 animate-spin'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                        >
                                            <circle
                                                className='opacity-25'
                                                cx='12'
                                                cy='12'
                                                r='10'
                                                stroke='currentColor'
                                                strokeWidth='4'
                                            />
                                            <path
                                                className='opacity-75'
                                                fill='currentColor'
                                                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                            />
                                        </svg>
                                    )}
                                    {isResending ? 'Sending...' : 'Resend Email'}
                                </button>
                            </div>
                            )}
                        </div>
                    </div>
                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Uninsured Expenses
                            </label>
                            <div className='flex flex-col'>
                                {patient?.medical_expenses &&
                                    patient?.medical_expenses.map((item: { name: string } | number, i: number) => {
                                        const name = typeof item === 'object' ? item.name : item;
                                        return <span key={i}>{name}</span>;
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <label className='text-[12px]/[16px] text-[#949599]'>
                        Primary Medical Issue Notes
                    </label>
                    <div>{campaignRequest?.medical_issue_notes}</div>
                </div>
            </div>

            <MedicalInfoDrawer
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                patient={patient}
                application={application}
                campaignId={campaign?.campaign_id}
                campaignRequestId={campaignRequest?.id}
                applicationId={application?.id}
                onUpdate={(response) => {
                    // Every medical-info save endpoint echoes back the updated
                    // patient (and sometimes the application), so paint those
                    // straight away rather than making the user wait on the
                    // full campaign refetch below.
                    if (campaignDispatch && (response?.patient || response?.application)) {
                        campaignDispatch({
                            type: 'set_medical_info',
                            payload: {
                                patient: response.patient,
                                application: response.application,
                            },
                        });
                    }

                    // Still refetch so anything the save touched indirectly
                    // (completion status, notes) catches up.
                    onDataUpdate?.();
                }}
            />
            
            {campaignRequest && (
                <MedicalReasonDrawer
                    isOpen={isMedicalReasonDrawerOpen}
                    setIsOpen={setIsMedicalReasonDrawerOpen}
                    campaignRequest={campaignRequest}
                    campaign={campaign}
                    patient={patient}
                    metadata={metadata}
                    onUpdate={onDataUpdate}
                />
            )}

            <PatientVerificationDrawer
                isOpen={isVerifyDrawerOpen}
                setIsOpen={setIsVerifyDrawerOpen}
                patient={patient}
                application={application}
                campaign={campaign}
                campaignId={campaignRequest?.campaign?.id ?? campaign?.id}
                metadata={metadata}
                onUpdate={onDataUpdate}
            />
        </>
    );
};

export default MedicalInfoTab;
